import {createSelector} from "reselect";
import {Company, CompanyEntity, CompanyType} from "../../../model/company";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {contains, firstOrUndefined} from "../../../util/util";

export type CompanySelectorProps = {
  regionId?: string
  companyType?: CompanyType;
  activeOnly?: boolean;
  showDoNotUse?: boolean;
};

const getRegionIdParam = createParamSelector<CompanySelectorProps | undefined, string | undefined>(params => params?.regionId);
const getCompanyTypeParam = createParamSelector<CompanySelectorProps | undefined, CompanyType | undefined>(params => params?.companyType);
const getActiveOnlyParam = createParamSelector<CompanySelectorProps | undefined, boolean | undefined>(params => params?.activeOnly);
const getShowDoNotUseParam = createParamSelector<CompanySelectorProps | undefined, boolean | undefined>(params => params?.showDoNotUse);

export const companiesSelector = () => createSelector(
  [getUseQueryResultData<Company[]>, getRegionIdParam, getCompanyTypeParam, getActiveOnlyParam, getShowDoNotUseParam],
  (data, regionId, companyType, activeOnly, showDoNotUse) => {
    return selectCompaniesAndTransform(
      data ?? [], {
        regionId: regionId,
        companyType: companyType,
        activeOnly: activeOnly,
        showDoNotUse: showDoNotUse
      }
    );
  }
);

export const companySelector = () => createSelector(
  [getUseQueryResultData<Company>],
  (data) => {
    return data ? transformCompany(data) : undefined;
  }
);

export const selectCompanies = (values?: Company[], selector?: CompanySelectorProps): Company[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.regionId) {
      if (!contains(v.companyRegionProfileList, profile => profile.regionId === selector.regionId)) {
        filterIn = false;
      }

      const activeOnly = selector?.activeOnly ?? false;
      if (filterIn && activeOnly) {
        const profile = firstOrUndefined(v.companyRegionProfileList?.filter((p) => p.regionId === selector.regionId));
        if (!profile || !profile.active) {
          filterIn = false;
        }
      }
    }

    if (selector?.companyType) {
      if (selector.companyType.code !== v.type) {
        filterIn = false;
      }
    }

    const showDoNotUse = selector?.showDoNotUse ?? false;
    if (filterIn && !showDoNotUse && v.doNotUse) {
      filterIn = false;
    }

    return filterIn;
  });
};

export const transformCompany = (value: Company): CompanyEntity => {
  return CompanyEntity.from(value)
}

export const selectCompaniesAndTransform = (values?: Company[], selector?: CompanySelectorProps): CompanyEntity[] => {
  const selected = selectCompanies(values, selector);
  return selected.map(transformCompany);
};
